import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { UserModel } from '@fi-app/user/user.model';
import { DataFieldModel } from '@fi-app/data-field/data-field.model';
import {
    DataFieldEditLanguagesComponent_Settings,
    TDataFieldEditLanguagesComponent_Settings,
} from '@fi-app/data-field/data-field-edit/languages/languages.component';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import {
    AvailableDateFormats,
    AvailableDateTimeLocale,
    AvailableTimeFormats,
    DefaultFormatDateArgs,
    fiGetUserPreferredDateFormat,
    fiGetUserPreferredTimeFormat,
    IAvailableDateTimeFormat,
    isDefined,
} from '@fi-shared/utils';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-user-edit-setting-tab',
    templateUrl: './fi-user-edit-setting-tab.component.html',
    styleUrls: ['./fi-user-edit-setting-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-user-edit-setting-tab' },
    providers: [
        {
            provide: DataFieldEditLanguagesComponent_Settings,
            deps: [FiUserEditSettingTabComponent],
            useFactory: (
                userEditComponent: FiUserEditSettingTabComponent,
            ): TDataFieldEditLanguagesComponent_Settings => ({
                applicationLanguages: userEditComponent.applicationLanguages,
                contentLanguages: userEditComponent.contentLanguages,
                onApplicationLanguageChangeFunc: ($event) => userEditComponent.onApplicationLanguageChange($event),
                isRadioButtonLayout: true,
            }),
        },
        { provide: FiComponentsContainer, useExisting: FiUserEditSettingTabComponent },
    ],
    standalone: false,
})
export class FiUserEditSettingTabComponent extends FiComponentsContainer implements OnInit {
    @Input() user: UserModel;
    @Input() preferredDateFormat: AvailableDateTimeLocale;
    @Input() preferredTimeFormat: AvailableDateTimeLocale;
    @Input() dataFieldsLang: DataFieldModel[] = [];
    @Input() applicationLanguages: any[];
    @Input() contentLanguages: any[];
    @Output() preferredDateFormatChange = new EventEmitter<AvailableDateTimeLocale>();
    @Output() preferredTimeFormatChange = new EventEmitter<AvailableDateTimeLocale>();
    @Output() applicationLanguageChange = new EventEmitter<FiApplicationLanguageType>();

    onApplicationLanguageChange(newApplicationLanguageType: FiApplicationLanguageType) {
        this.applicationLanguageChange.emit(newApplicationLanguageType);
    }

    constructor(
        private fiUserDataService: FiUserDataService,
        private fiFormatDateService: FiFormatDateService,
        private fiCustomerDataService: FiCustomerDataService,
    ) {
        super();
    }

    ngOnInit() {
        this._initLanguages();
        if (this.isPreferredDateTimeFormatAvailable()) this.setPreferredDateTimeFormat();
    }

    private _initLanguages() {
        if (!this.user.ApplicationLanguageType) {
            this.user.ApplicationLanguageType = this.fiUserDataService.userData.ApplicationLanguage;
            this.onApplicationLanguageChange(this.user.ApplicationLanguageType);
        }

        if (!this.user.PreferredContentLanguageId)
            this.user.PreferredContentLanguageId = this.fiUserDataService.userData.PreferredContentLanguage;
    }

    private setPreferredDateTimeFormat() {
        if (!isDefined(this.preferredDateFormat)) {
            this.preferredDateFormat = fiGetUserPreferredDateFormat(
                DefaultFormatDateArgs.applicationLanguageType,
            ).locale;
            this.onPreferredDateFormatChange();
        }
        if (!isDefined(this.preferredTimeFormat)) {
            this.preferredTimeFormat = fiGetUserPreferredTimeFormat(
                DefaultFormatDateArgs.applicationLanguageType,
            ).locale;
            this.onPreferredTimeFormatChange();
        }
    }

    onPreferredDateFormatChange() {
        this.preferredDateFormatChange.emit(this.preferredDateFormat);
    }

    onPreferredTimeFormatChange() {
        this.preferredTimeFormatChange.emit(this.preferredTimeFormat);
    }

    isPreferredDateTimeFormatAvailable(): boolean {
        return !!this.user?.Id;
    }

    getTimeFormats() {
        return this.fiFormatDateService.timeFormas;
    }

    protected readonly AvailableDateFormats: IAvailableDateTimeFormat[] = AvailableDateFormats;
}
